import AutoGrowTextarea from "@/components/AutoGrowTextarea/AutoGrowTextarea"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import EmptyState from "@/components/EmptyState/EmptyState"
import FacilitatorPanel from "@/components/FacilitatorPanel/FacilitatorPanel"
import JobStatement from "@/components/JobStatement/JobStatement"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import PaperNote from "@/components/PaperNote/PaperNote"
import { JOB_COPY } from "@/content/job"
import { PAINS_COPY } from "@/content/pains"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { updateJob } from "@/services/jobs"
import { useSessionStore } from "@/store/sessionStore"
import {
  jobHolds,
  painPositions,
  painRank,
  painsForJob,
  peopleFor,
  selectJob,
  selectPains,
  selectSession,
} from "@/store/selectors"
import { isExpired } from "@/utils/sessionLifecycle"
import type { Job as JobDoc, JobInput, Phase } from "@/types"
import styles from "@/styles/pages/Job.module.css"

/*
 * One job, one sentence.
 *
 * The facilitator writes it, not the room. That is a deliberate narrowing of the
 * prototype, which let everyone type: `updateJob` is facilitator-only, and a
 * sentence six people are editing at once is how a room spends ten minutes
 * producing something vaguer than what one person would have written. The room's
 * part was the vote.
 *
 * The room still watches it appear, live, which is the point of reading it back
 * to them — and `selectJobHolds` is what stops the deck opening on half of it.
 */

interface JobProps {
  phase: Phase
  canDrive: boolean
  /** The round is being read back. The sentence is shown, never edited. */
  readOnly: boolean
}

export default function Job({ phase, canDrive, readOnly }: JobProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const pains = useSessionStore(selectPains)
  const participants = useSessionStore((s) => s.participants)
  const now = useExpiryTick([session?.expiresAt ?? null])

  if (!session) return null

  const expired = isExpired(session, now)
  const userStory = session.config.userStoryEnabled
  const holds = jobHolds(job, userStory)
  const locked = phase === "locked"
  const from = painsForJob(pains, job)
  // `from` is a handful of pains out of the board, so its own index means
  // nothing — the note the room voted for has to be the colour it was there.
  const positions = painPositions(pains)

  // The tally's own label — "The winner", "Tied 1st" — carried onto the note
  // that fed this job, so the room still sees the fact that sent it here. Same
  // bands `PainsBoard` badges, read off the same votes.
  const rankTag = (painId: string) => {
    const rank = painRank(pains, painId)
    if (!rank) return null
    if (rank.band === "top") return rank.tied ? PAINS_COPY.tiedFirst : PAINS_COPY.theWinner
    if (rank.band === "runnerUp") return PAINS_COPY.runnerUp
    return PAINS_COPY.third
  }

  const showControls = canDrive && !expired

  return (
    <div className={styles.job}>
      {/* The heading keeps the rail's width clear of it on a wide window, which
          is all this wrapper is for — hence no class of its own when there is
          no rail beside it. */}
      <div className={showControls ? styles.headingRowControls : undefined}>
        <RoundHeading
          title={JOB_COPY.heading}
          objective={JOB_COPY.objective}
          objectiveNowrap
          status={
            locked ? JOB_COPY.lockedHint : holds ? JOB_COPY.holdsHint : JOB_COPY.incompleteHint
          }
          statusInline
        />
      </div>

      <div className={styles.columns}>
        {/*
         * The pain stays on screen while the sentence is written. Without it the
         * job drifts off the thing the room actually voted for — which is the
         * failure this round exists to prevent.
         */}
        <section className={styles.from}>
          {/* Singular case is unlabelled: the note's own corner tag ("The
              winner") already says why it is here. */}
          {from.length > 1 ? <p className={styles.fromLabel}>{JOB_COPY.fromPains}</p> : null}
          {from.length === 0 ? (
            <EmptyState title={PAINS_COPY.emptyBoard} detail={JOB_COPY.unwritten} />
          ) : (
            from.map((pain) => (
              <PaperNote
                key={pain.id}
                position={positions.get(pain.id) ?? 0}
                size="sm"
                cornerTag={rankTag(pain.id)}
              >
                <p className={styles.painText}>{pain.text}</p>
                {pain.voterIds.length > 0 ? (
                  <span className={styles.painFoot}>
                    <span className={styles.painFootCount}>
                      {PAINS_COPY.noteVoteCount(pain.voterIds.length)}
                    </span>
                    <span className={styles.painFootRight}>
                      <AvatarStack
                        people={peopleFor(participants, pain.voterIds)}
                        size={28}
                        max={4}
                      />
                    </span>
                  </span>
                ) : null}
              </PaperNote>
            ))
          )}
        </section>

        <section className={styles.statement}>
          {/* Three ways to arrive at the read-back: the job is locked, this
              browser is not the one writing it, or the round is being looked at
              from further on. They all want the same panel. */}
          {locked || !canDrive || readOnly ? (
            /*
             * The room watches the sentence appear rather than waiting on a
             * blank panel — reading it back to them is a prompt in the rail, and
             * it only works if they can see what is being read.
             */
            <StatementPreview
              who={job?.who ?? ""}
              what={job?.what ?? ""}
              why={job?.why ?? ""}
              userStory={userStory}
              holds={holds}
              job={job}
            />
          ) : (
            <>
              <StatementFields
                key={job?.id ?? "none"}
                sessionId={session.id}
                jobId={job?.id ?? null}
                who={job?.who ?? ""}
                what={job?.what ?? ""}
                why={job?.why ?? ""}
                userStory={userStory}
              />
              {/* What the room is seeing, so the facilitator reads the same words. */}
              {holds ? <JobStatement job={job} userStoryEnabled={userStory} /> : null}
            </>
          )}
        </section>

        {showControls ? (
          <div className={styles.controls}>
            <FacilitatorPanel canDrive />
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** The sentence as it stands, for everyone who is not writing it. */
function StatementPreview({
  who,
  what,
  why,
  userStory,
  holds,
  job,
}: {
  who: string
  what: string
  why: string
  userStory: boolean
  holds: boolean
  job: JobDoc | null
}) {
  if (holds) return <JobStatement job={job} userStoryEnabled={userStory} />

  const parts = userStory
    ? [
        { label: JOB_COPY.whoLabel, value: who },
        { label: JOB_COPY.whatLabel, value: what },
        { label: JOB_COPY.whyLabel, value: why },
      ]
    : [{ label: JOB_COPY.plainLabel, value: what }]

  // Nothing typed at all is a wait, not a half-filled form.
  if (parts.every((part) => !part.value.trim())) {
    return <EmptyState title={JOB_COPY.waiting} detail={JOB_COPY.incompleteHint} />
  }

  return (
    <dl className={styles.preview}>
      {parts.map((part) => (
        <div key={part.label} className={styles.previewRow}>
          <dt className={styles.fieldLabel}>{part.label}</dt>
          <dd className={part.value.trim() ? styles.previewValue : styles.previewBlank}>
            {part.value.trim() || "…"}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/*
 * The fields, keyed by job id at the call site.
 *
 * Remounting when the job changes matters here for the same reason it does in the
 * facilitator's notes: `useDebouncedField` holds a draft, and the job gate can
 * put a second job on screen while the first one's last keystroke is still
 * pending. Keyed, that keystroke lands on the job it was typed into.
 */
function StatementFields({
  sessionId,
  jobId,
  who,
  what,
  why,
  userStory,
}: {
  sessionId: string
  jobId: string | null
  who: string
  what: string
  why: string
  userStory: boolean
}) {
  const { write } = useFirestoreWrite()
  const save = (patch: JobInput) => {
    if (jobId) write(() => updateJob(sessionId, jobId, patch))
  }

  const whoField = useDebouncedField({ value: who, onCommit: (value) => save({ who: value }) })
  const whatField = useDebouncedField({ value: what, onCommit: (value) => save({ what: value }) })
  const whyField = useDebouncedField({ value: why, onCommit: (value) => save({ why: value }) })

  if (!userStory) {
    return (
      <div className={styles.fields}>
        <Field
          label={JOB_COPY.plainLabel}
          placeholder={JOB_COPY.plainPlaceholder}
          field={whatField}
        />
      </div>
    )
  }

  // Labels are the sentence, so they read in order down the column: "As a … I
  // want … so that …". That is what makes the missing third of it obvious.
  return (
    <div className={styles.fields}>
      <Field label={JOB_COPY.whoLabel} placeholder={JOB_COPY.whoPlaceholder} field={whoField} />
      <Field label={JOB_COPY.whatLabel} placeholder={JOB_COPY.whatPlaceholder} field={whatField} />
      <Field label={JOB_COPY.whyLabel} placeholder={JOB_COPY.whyPlaceholder} field={whyField} />
    </div>
  )
}

function Field({
  label,
  placeholder,
  field,
}: {
  label: string
  placeholder: string
  field: {
    value: string
    onChange: (event: { target: { value: string } }) => void
    onBlur: () => void
  }
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <AutoGrowTextarea
        className={styles.fieldInput}
        placeholder={placeholder}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    </label>
  )
}
